import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import {
  Badge, Button, Empty, Input, Loading, Row,
} from '../../components/Ui';
import { Reveal, Pop } from '../../components/Motion';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';
import {
  salaryText, shortDate, timeAgo, titleCase,
} from '../../utils/format';
import OnboardingQuestions, { missingRequired } from '../../components/OnboardingQuestions';

const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [job, setJob] = useState(null);
  const [applied, setApplied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ coverLetter: '', experienceYears: '', expectedSalary: '' });
  /*
   * What this application asks: admin's questions (same on every job) plus the
   * ones this recruiter added to their own job.
   */
  const [askFields, setAskFields] = useState([]);
  const [answers, setAnswers] = useState({});
  useEffect(() => {
    api.onboarding.fields({ form: 'application' }).then((r) => setAskFields(r.data || [])).catch(() => setAskFields([]));
  }, []);
  const questions = [...askFields, ...((job && job.extraFields) || [])];
  const shows = (key) => !askFields.length || askFields.some((f) => f.builtin === key);
  const labelFor = (key, fallback) => (askFields.find((f) => f.builtin === key) || {}).label || fallback;

  useEffect(() => {
    setLoading(true);
    api.jobs.detail(id)
      .then((res) => { setJob(res.data.job); setApplied(res.data.hasApplied); })
      .catch(() => setJob(null))
      .finally(() => setLoading(false));
  }, [id]);

  const apply = async (e) => {
    e.preventDefault();
    setApplying(true);
    try {
      const missing = missingRequired(questions, answers);
      if (missing) { toast.error(`Please add: ${missing}`, 'One more thing'); setApplying(false); return; }
      await api.jobs.apply(id, {
        answers,
        coverLetter: form.coverLetter,
        experienceYears: Number(form.experienceYears) || 0,
        expectedSalary: Number(form.expectedSalary) || 0,
      });
      setApplied(true);
      setShowForm(false);
      toast.success('The recruiter has received your application. Track it under My Activity.', 'Application sent');
    } catch (err) {
      toast.error(err.message, 'Could not apply');
    } finally {
      setApplying(false);
    }
  };

  if (loading) return <Loading text="Loading job…" />;
  if (!job) {
    return <Empty emoji="💼" title="Job not found" actionLabel="Go back" onAction={() => navigate(-1)} />;
  }

  return (
    <div className="has-action-bar">
      <PageHeader title="Job details" subtitle={job.companyName} />

      <div className="split">
        <div>
          <Reveal className="card sh-sm">
            <h1 className="h2">{job.title}</h1>
            <div className="t-body" style={{ marginTop: 4 }}>{job.companyName}</div>
            <div className="t-tiny" style={{ marginTop: 3 }}>
              Posted {timeAgo(job.createdAt)} · {job.viewCount || 0} views
            </div>

            <div className="row wrap" style={{ gap: 8, marginTop: 14 }}>
              <span className="pill">{titleCase(job.jobType)}</span>
              {job.city ? <span className="pill">📍 {job.city}</span> : null}
              {job.openings > 1 ? <span className="pill">{job.openings} openings</span> : null}
              <Badge value={job.status} />
            </div>

            <div
              style={{
                background: colors.successBg, borderRadius: 12, padding: 14, marginTop: 16,
              }}
            >
              <div className="t-tiny">SALARY</div>
              <div style={{ fontSize: 19, fontWeight: 800, color: colors.success, marginTop: 2 }}>
                {salaryText(job)}
              </div>
            </div>
          </Reveal>

          <Reveal delay={70} className="card sh-sm" style={{ marginTop: 16 }}>
            <h3 className="t-title" style={{ marginBottom: 8 }}>Job details</h3>
            <Row
              label="Experience"
              value={job.experienceMax ? `${job.experienceMin} – ${job.experienceMax} years` : 'Fresher welcome'}
            />
            <Row label="Job type" value={titleCase(job.jobType)} />
            <Row label="Location" value={job.address || job.city || '—'} />
            <Row label="Applications" value={String(job.applicationCount || 0)} />
            {job.expiresAt ? <Row label="Apply before" value={shortDate(job.expiresAt)} /> : null}
          </Reveal>

          {job.description ? (
            <Reveal delay={120} className="card sh-sm" style={{ marginTop: 16 }}>
              <h3 className="t-title" style={{ marginBottom: 8 }}>Description</h3>
              <p className="t-body" style={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>{job.description}</p>
            </Reveal>
          ) : null}

          {job.responsibilities && job.responsibilities.length ? (
            <Reveal delay={160} className="card sh-sm" style={{ marginTop: 16 }}>
              <h3 className="t-title" style={{ marginBottom: 8 }}>Responsibilities</h3>
              {job.responsibilities.map((r) => (
                <div key={r} className="row" style={{ gap: 9, alignItems: 'flex-start', marginBottom: 6 }}>
                  <span style={{ color: colors.primary }}>•</span>
                  <span className="t-body grow">{r}</span>
                </div>
              ))}
            </Reveal>
          ) : null}

          {job.skills && job.skills.length ? (
            <Reveal delay={200} className="card sh-sm" style={{ marginTop: 16 }}>
              <h3 className="t-title" style={{ marginBottom: 10 }}>Skills required</h3>
              <div className="row wrap" style={{ gap: 8 }}>
                {job.skills.map((s) => (
                  <span
                    key={s}
                    style={{
                      background: colors.primaryLight, color: colors.primary,
                      padding: '6px 11px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
                    }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </Reveal>
          ) : null}
        </div>

        {/* Apply panel */}
        <div className="split-side">
          {applied ? (
            <div className="action-bar" style={{ textAlign: 'center' }}>
              <div className="action-main">
                <span style={{ fontSize: 22 }}>✅</span>
              </div>
              <div className="action-cta">
                <div className="t-title" style={{ color: colors.success }}>Already applied</div>
                <button
                  type="button"
                  className="link-btn"
                  style={{ marginTop: 2 }}
                  onClick={() => navigate('/my-applications')}
                >
                  Track my applications
                </button>
              </div>
            </div>
          ) : showForm ? (
            <Pop className="card sh-md" style={{ borderColor: colors.primary }}>
              <h3 className="t-title" style={{ marginBottom: 12 }}>Your application</h3>
              <Row label="Name" value={user && user.name} />
              <Row label="Phone" value={`+91 ${user && user.phone}`} />
              <div className="divider" />
              <form onSubmit={apply}>
                {shows('experienceYears') ? (
                  <Input
                    label={labelFor('experienceYears', 'Years of experience')}
                    inputMode="numeric"
                    placeholder="0 for fresher"
                    value={form.experienceYears}
                    onChange={(e) => setForm({ ...form, experienceYears: e.target.value.replace(/\D/g, '') })}
                  />
                ) : null}
                {shows('expectedSalary') ? (
                  <Input
                    label={labelFor('expectedSalary', 'Expected salary (optional)')}
                    inputMode="numeric"
                    placeholder="e.g. 15000"
                    value={form.expectedSalary}
                    onChange={(e) => setForm({ ...form, expectedSalary: e.target.value.replace(/\D/g, '') })}
                  />
                ) : null}
                <Input
                  label="Why are you a good fit? (optional)"
                  textarea
                  rows={4}
                  placeholder="A short note for the recruiter"
                  value={form.coverLetter}
                  onChange={(e) => setForm({ ...form, coverLetter: e.target.value })}
                />
                <OnboardingQuestions form="application" fields={questions} answers={answers} onChange={setAnswers} />
                <Button title="Submit application" type="submit" loading={applying} full />
                <Button
                  title="Cancel"
                  variant="light"
                  full
                  style={{ marginTop: 8 }}
                  onClick={() => setShowForm(false)}
                />
              </form>
            </Pop>
          ) : (
            <div className="action-bar">
              <div className="action-main">
                <div className="t-tiny">SALARY</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: colors.success }}>
                  {salaryText(job)}
                </div>
              </div>

              <div className="action-cta">
                <Button
                  title="Apply for this job"
                  icon="📤"
                  full
                  disabled={job.status !== 'open'}
                  onClick={() => setShowForm(true)}
                />
              </div>

              <div className="action-extra">
                {job.status !== 'open' ? (
                  <div className="t-tiny" style={{ textAlign: 'center', marginTop: 8 }}>
                    This job is no longer accepting applications.
                  </div>
                ) : null}
                {job.contactPhone ? (
                  <Button
                    title="Call recruiter"
                    icon="📞"
                    variant="ghost"
                    full
                    style={{ marginTop: 10 }}
                    onClick={() => { window.location.href = `tel:${job.contactPhone}`; }}
                  />
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetailPage;
